import IJobSection from "../../interfaces/IJobSection";
import { BoxHeader } from "./box-header";
import * as React from "react";
import { GoTerm } from "../../../../api/openapi-generator";
import { LogoUniprot } from "./logo-uniprot";
import { addPubmedLinksIntoPlainText } from "../../../../utils/toolsReact";
import { splitSentences } from "../../../../utils/tools";
import PublicationButton from "../PublicationButton";
import PublicationParagraph from "../PublicationParagraph";
import BinaryInteractionTable from "../BinaryInteractionTable";
import { CheckedSpinner } from "./checked-spinner";
import { useResultsContext } from "../../context/results-context";
import { Section } from "./section";

export function SectionAdditionalInformation({section}: {section: IJobSection}) {
  const {results, uniprot} = useResultsContext();
  const loading = uniprot === null;
  const nonEmptyArr: (arr: any) => boolean = (arr: any) => Array.isArray(arr) && arr.length > 0;
  return (
    <Section section={section} style={{marginTop: '1rem'}}>
      <LogoUniprot accession={results.uniprotAccession}/>
      <BoxHeader section={section} />
      {loading ? (
        <div className="ll-section-body ll-section-spinner">
          <CheckedSpinner withText={true}/>
        </div>
      ) : (
        <div className="ll-section-body">

          <h3>UniProtKB</h3>
          <div className="ll-additional-information-subsection">
            <p>UniProtKB accession for the wild-type protein is <a href={`https://www.uniprot.org/uniprot/${results.uniprotAccession}`} target="_blank">{results.uniprotAccession}</a>. The following information is extracted from the UniProtKB entry.</p>
          </div>

          {nonEmptyArr(uniprot.functionDescriptions) && <>
              <h3>Function</h3>
              <div className="ll-additional-information-subsection">
                {uniprot.functionDescriptions
                  .map(addPubmedLinksIntoPlainText)
                  .map((t, i) => <p key={i}>{t}</p>)}
              </div>
          </>}

          {uniprot.subunit && <>
              <h3>Subunit interactions</h3>
              <div className="ll-additional-information-subsection">
                {splitSentences(uniprot.subunit.text)
                  .map(addPubmedLinksIntoPlainText)
                  .map((t, i) => <p key={i}>{t}</p>)}
                  <div className="ll-screen">
                      <PublicationButton
                          publications={uniprot.subunit.publications}
                          allPublications={uniprot.publications}
                          buttonStyle={{ marginTop: '0.2rem' }}
                      />
                  </div>
                  <div className="ll-print">
                      <PublicationParagraph
                          publications={uniprot.subunit.publications}
                          allPublications={uniprot.publications}
                      />
                  </div>
              </div>
          </>}

          {uniprot.binaryInteractions && nonEmptyArr(uniprot.binaryInteractions) && <>
              <h3>Protein-protein interactions</h3>
              <div style={{ margin: '0 2rem' }}>
                  <BinaryInteractionTable
                      interactions={uniprot.binaryInteractions}
                  />
              </div>
          </>}

          {nonEmptyArr(uniprot.diseases) && <>
              <h3>Protein involvement in disease</h3>
              <div className="ll-additional-information-subsection">
                {uniprot.diseases.map((disease, idx) => (
                  <div key={`${idx}_${disease.acronym}`} className="ll-disease">
                    {(disease.name || disease.acronym) && (
                      <h4>
                        {disease.name && disease.acronym ? `${disease.name} (${disease.acronym})` : `${disease.name}${disease.acronym}`}
                        <span className="ll-screen">
                          <PublicationButton
                            publications={disease.publications}
                            allPublications={uniprot.publications}
                            buttonStyle={{ marginLeft: '0.5rem' }}
                            small
                          />
                        </span>
                      </h4>
                    )}
                    <div className="ll-print">
                      <PublicationParagraph
                        publications={disease.publications}
                        allPublications={uniprot.publications}
                      />
                    </div>
                    {disease.texts.map((text, textIdx) => <p key={textIdx}>{text}{text.length > 0 && text[text.length - 1] != '.' ? '.' : ''}</p>)}
                    {disease.description && <p>Disease description: {disease.description}</p>}
                  </div>
                ))}
              </div>
          </>}

          <GoTerms
            title={"GO - Molecular function"}
            goTerms={uniprot.molecularFunctions}
            accession={results.uniprotAccession}
          />
          <GoTerms
            title={"GO - Biological process"}
            goTerms={uniprot.biologicalProcesses}
            accession={results.uniprotAccession}
          />
        </div>
      )}
    </Section>
  )
}

function GoTerms({title, goTerms, accession}: {title: string, goTerms: GoTerm[], accession: string}) {
  return goTerms.length > 0 ? <>
    <h3>{title}</h3>
    <div className="ll-additional-information-subsection">
      <ul>
        {goTerms.map(item => (
          <li key={item.id}><a href={`https://www.ebi.ac.uk/QuickGO/term/${item.id}`} target="_blank">
            <span className="ll-print">{item.id} - </span>{item.term}
          </a></li>
        ))}
      </ul>
      <p className="ll-screen">
        <a href={`https://www.ebi.ac.uk/QuickGO/annotations?geneProductId=${accession}`} target="_blank">
          Complete GO annotation on QuickGO ...
        </a>
      </p>
    </div>
  </> : null
}